// ==============================================
// MyPolaris — SD Card Manager
// ==============================================
//
// Browse the SD card in a Scout and free up space, from anywhere, without
// a file manager on the Scout itself. The page never talks to the boat: it
// shows the last scan the Launcher uploaded and queues delete/rescan
// commands the Launcher runs on its next poll. While this page is open the
// Scout polls every few seconds (session keep-alive), so actions land in
// seconds; when the boat is offline they run once it reconnects.
// Package-gated via packages.sd_card_manager (upsell card, sidebar hidden).
//
// Readability mandate: nothing under 14px (text-sm), primary copy 16px+.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CircleStackIcon, FolderIcon, DocumentIcon, TrashIcon, SparklesIcon, ArrowPathIcon,
  LockClosedIcon, ExclamationTriangleIcon, ChevronRightIcon, SignalIcon, SignalSlashIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import { apiErrorMessage } from './DriveChartsPage';
import { SD_PROTECTED_ROOTS } from '../../types/sdCard';
import type { SdNode, SdDeviceSummary, SdCommand } from '../../types/sdCard';

const SESSION_KEEPALIVE_MS = 30_000;
const SCAN_POLL_MS = 3_000;
const ONLINE_WINDOW_MS = 3 * 60_000;

export const fmtBytes = (b: number | null | undefined) => {
  if (b === null || b === undefined) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = b, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i >= 3 ? 1 : 0)} ${u[i]}`;
};
const fmtDate = (ms: number) => new Date(ms).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtAgo = (iso: string | null | undefined) => {
  if (!iso) return 'never';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
};
const isOnline = (lastSeen: string | null | undefined) => !!lastSeen && Date.now() - new Date(lastSeen).getTime() < ONLINE_WINDOW_MS;
const deviceLabel = (d: SdDeviceSummary) => d.vesselName || d.name || d.serialNumber;

/** Walk the tree along a path of folder names. */
export function nodeAt(root: SdNode | null | undefined, path: string[]): SdNode | null {
  let cur: SdNode | null | undefined = root;
  for (const seg of path) {
    cur = cur?.c?.find((n) => n.t === 'd' && n.n === seg);
    if (!cur) return null;
  }
  return cur ?? null;
}

const isActive = (c: SdCommand) => c.status === 'pending' || c.status === 'delivered' || c.status === 'executing';

export function SdCardPage() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [path, setPath] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<SdNode[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: access, isLoading: accessLoading } = useQuery({ queryKey: ['sd-access'], queryFn: () => api.sdAccess() });

  const { data: devices, isLoading: devicesLoading } = useQuery({
    queryKey: ['sd-devices'],
    queryFn: () => api.sdDevices(),
    enabled: access?.enabled === true,
    refetchInterval: 30_000,
  });

  // Pick a Scout: the one most recently seen.
  useEffect(() => {
    if (!deviceId && devices?.length) setDeviceId(devices[0].deviceId);
  }, [devices, deviceId]);
  const device = devices?.find((d) => d.deviceId === deviceId) ?? null;

  // Keep-alive: makes the Scout poll fast while this page is open, and
  // queues a rescan if the card hasn't been scanned recently.
  useEffect(() => {
    if (!deviceId || access?.enabled !== true) return;
    let stop = false;
    const tick = () => { if (!stop) api.sdSession(deviceId).catch(() => undefined); };
    tick();
    const t = setInterval(tick, SESSION_KEEPALIVE_MS);
    return () => { stop = true; clearInterval(t); };
  }, [deviceId, access?.enabled]);

  const { data: scanData, isLoading: scanLoading } = useQuery({
    queryKey: ['sd-scan', deviceId],
    queryFn: () => api.sdScan(deviceId as string),
    enabled: !!deviceId && access?.enabled === true,
    refetchInterval: SCAN_POLL_MS,
  });
  const scan = scanData?.scan ?? null;
  const commands = scanData?.commands ?? [];
  const activeDeletes = commands.filter((c) => c.type === 'sd.delete' && isActive(c));
  const deletingPaths = useMemo(() => new Set(activeDeletes.flatMap((c) => c.payload?.paths ?? [])), [activeDeletes]);
  const scanning = commands.some((c) => c.type === 'sd.scan' && isActive(c));

  // Surface command failures once.
  const [seenFailed, setSeenFailed] = useState<Set<string>>(new Set());
  useEffect(() => {
    for (const c of commands) {
      if (c.status === 'failed' && !seenFailed.has(c.id)) {
        setSeenFailed((s) => new Set(s).add(c.id));
        const msg = (c.result as { error?: string } | null)?.error;
        addToast({ type: 'error', title: c.type === 'sd.delete' ? 'Delete failed on the Scout' : 'Scan failed on the Scout', message: msg });
      }
    }
  }, [commands, seenFailed, addToast]);

  // Reset browsing state when the device changes.
  useEffect(() => { setPath([]); setSelected(new Set()); setError(null); }, [deviceId]);

  const folder = nodeAt(scan?.tree, path);
  const rows = useMemo(() => [...(folder?.c ?? [])].sort((a, b) => b.s - a.s), [folder]);
  const rowsTotal = rows.reduce((a, r) => a + r.s, 0) || 1;
  const relPath = (n: SdNode) => [...path, n.n].join('/');
  const protectedRow = (n: SdNode) => path.length === 0 && SD_PROTECTED_ROOTS.has(n.n);

  const rescan = useMutation({
    mutationFn: () => api.sdRescan(deviceId as string),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['sd-scan', deviceId] });
      addToast({ type: 'success', title: r.already_pending ? 'A scan is already on its way' : 'Rescan requested', message: 'The Scout will report back in a few seconds.' });
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not request a rescan.')),
  });

  const del = useMutation({
    mutationFn: (paths: string[]) => api.sdDelete(deviceId as string, paths),
    onSuccess: (_r, paths) => {
      setConfirm(null);
      setSelected(new Set());
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['sd-scan', deviceId] });
      addToast({
        type: 'success',
        title: `Deleting ${paths.length} item${paths.length === 1 ? '' : 's'}`,
        message: isOnline(device?.lastSeenAt) ? 'The Scout is removing them now.' : 'The Scout is offline — it will run as soon as it reconnects.',
      });
    },
    onError: (err) => { setConfirm(null); setError(apiErrorMessage(err, 'Could not queue the delete.')); },
  });

  const toggle = (n: SdNode) => setSelected((s) => { const t = new Set(s); const k = relPath(n); t.has(k) ? t.delete(k) : t.add(k); return t; });
  const selectedNodes = rows.filter((r) => selected.has(relPath(r)));
  const selectedBytes = selectedNodes.reduce((a, r) => a + r.s, 0);

  // ---- gate states ----
  if (accessLoading) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (access && !access.enabled) {
    return (
      <div className="p-6 max-w-xl mx-auto mt-10">
        <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-8 text-center">
          <SparklesIcon className="w-10 h-10 mx-auto text-amber-400 mb-3" />
          <h1 className="text-xl font-bold text-white mb-2">Manage Scout SD Storage</h1>
          <p className="text-slate-300 mb-1">See what's filling the SD card in your Scout and free up space from anywhere — no file manager needed on the boat.</p>
          <p className="text-slate-400 text-sm mb-6">Managing Scout SD storage isn't included in your current plan.</p>
          <a href="/dashboard/billing" className="inline-block px-5 py-2.5 rounded-lg bg-amber-400 text-slate-900 font-bold hover:brightness-105">
            See plans that include it
          </a>
        </div>
      </div>
    );
  }

  const online = isOnline(device?.lastSeenAt);
  const used = scan?.totalBytes != null && scan.freeBytes != null ? scan.totalBytes - scan.freeBytes : null;
  const usedPct = used != null && scan?.totalBytes ? Math.min(100, (100 * used) / scan.totalBytes) : 0;

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-6xl">
      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <CircleStackIcon className="w-6 h-6 text-amber-400" /> Manage Scout SD Storage
          </h1>
          <p className="text-base text-slate-300 mt-1">Browse the SD card in your Scout and delete what you no longer need. Changes are made by the Scout itself, usually within a few seconds.</p>
        </div>
        <button
          type="button"
          onClick={() => rescan.mutate()}
          disabled={!deviceId || rescan.isPending || scanning}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-700 text-white text-base font-semibold hover:bg-slate-600 disabled:opacity-60"
        >
          <ArrowPathIcon className={`w-5 h-5 ${scanning ? 'animate-spin' : ''}`} /> {scanning ? 'Scanning…' : 'Rescan'}
        </button>
      </div>

      {/* device picker */}
      {devicesLoading ? (
        <div className="text-slate-400 text-base">Finding your Scouts…</div>
      ) : !devices || devices.length === 0 ? (
        <div className="bg-slate-800/40 border border-dashed border-slate-700 rounded-2xl p-6 text-center text-slate-400 text-base">
          No Scouts on your account yet. Once a Scout is registered it will appear here.
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {devices.map((d) => {
            const on = isOnline(d.lastSeenAt);
            const u = d.totalBytes != null && d.freeBytes != null ? d.totalBytes - d.freeBytes : null;
            const pct = u != null && d.totalBytes ? Math.min(100, (100 * u) / d.totalBytes) : 0;
            const active = d.deviceId === deviceId;
            return (
              <button
                key={d.deviceId}
                type="button"
                onClick={() => setDeviceId(d.deviceId)}
                aria-pressed={active}
                className={`text-left min-w-[260px] flex-1 sm:flex-none rounded-2xl border-2 px-4 py-3 bg-slate-800/60 ${active ? 'border-amber-400' : 'border-slate-700 hover:border-slate-500'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-base font-bold text-white truncate">{deviceLabel(d)}</span>
                  <span className={`inline-flex items-center gap-1 text-sm font-medium ${on ? 'text-emerald-300' : 'text-slate-400'}`}>
                    {on ? <SignalIcon className="w-4 h-4" /> : <SignalSlashIcon className="w-4 h-4" />}{on ? 'Online' : `Seen ${fmtAgo(d.lastSeenAt)}`}
                  </span>
                </div>
                <div className="text-sm text-slate-300 mt-1">
                  {d.totalBytes != null ? <>{fmtBytes(u)} used of {fmtBytes(d.totalBytes)} · <span className="text-white font-semibold">{fmtBytes(d.freeBytes)} free</span></> : 'SD card not scanned yet'}
                </div>
                <div className="h-2.5 mt-2 rounded-full bg-slate-900 overflow-hidden">
                  <div className={`h-full ${pct > 90 ? 'bg-red-400' : 'bg-amber-400'}`} style={{ width: `${pct.toFixed(1)}%` }} />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {device && (
        <>
          {/* status line */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-300">
            {scan?.totalBytes != null && (
              <span><span className="text-white font-semibold">{fmtBytes(used)}</span> used of {fmtBytes(scan.totalBytes)} ({usedPct.toFixed(0)}%) · <span className="text-white font-semibold">{fmtBytes(scan.freeBytes)}</span> free</span>
            )}
            <span>Last scanned {fmtAgo(scan?.receivedAt)}</span>
            {!online && (
              <span className="inline-flex items-center gap-1 text-amber-300">
                <ExclamationTriangleIcon className="w-4 h-4" /> Scout is offline — deletes will run when it reconnects.
              </span>
            )}
            {scan?.truncated && <span className="text-amber-300">Some very deep folders were summarised.</span>}
          </div>

          {/* breadcrumbs + bulk actions */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <nav aria-label="Folder path" className="flex flex-wrap items-center gap-1 text-base">
              <button type="button" onClick={() => { setPath([]); setSelected(new Set()); }} className="text-amber-300 hover:underline font-semibold">SD card</button>
              {path.map((seg, i) => (
                <span key={i} className="flex items-center gap-1">
                  <ChevronRightIcon className="w-4 h-4 text-slate-500" />
                  <button type="button" onClick={() => { setPath(path.slice(0, i + 1)); setSelected(new Set()); }} className="text-amber-300 hover:underline font-semibold">{seg}</button>
                </span>
              ))}
            </nav>
            <div className="flex items-center gap-2">
              {selectedNodes.length > 0 && <span className="text-sm text-slate-300">{selectedNodes.length} selected · {fmtBytes(selectedBytes)}</span>}
              <button
                type="button"
                disabled={selectedNodes.length === 0 || del.isPending}
                onClick={() => setConfirm(selectedNodes)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-red-400/70 text-red-300 text-base font-semibold hover:bg-red-900/30 disabled:opacity-40"
              >
                <TrashIcon className="w-5 h-5" /> Delete selected
              </button>
            </div>
          </div>

          {error && (
            <p role="alert" className="text-sm font-medium text-red-300 bg-red-900/30 border border-red-800/60 rounded-lg px-3 py-2">{error}</p>
          )}

          {/* table */}
          {scanLoading && !scan ? (
            <div className="text-slate-400 text-base py-6">Loading…</div>
          ) : !scan?.tree ? (
            <div className="bg-slate-800/40 border border-dashed border-slate-700 rounded-2xl p-8 text-center text-base text-slate-300">
              <ArrowPathIcon className="w-8 h-8 mx-auto mb-2 text-amber-400 animate-spin" />
              {online ? 'Waiting for the Scout to scan its SD card — this takes a few seconds the first time.' : 'The Scout is offline. It will scan its SD card the next time it connects.'}
            </div>
          ) : !folder ? (
            <div className="text-slate-400 text-base py-6">That folder is no longer on the card.</div>
          ) : rows.length === 0 ? (
            <div className="bg-slate-800/40 border border-dashed border-slate-700 rounded-2xl p-6 text-center text-slate-400 text-base">This folder is empty.</div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-700 bg-slate-800/60">
              <table className="w-full text-base">
                <thead>
                  <tr className="text-left text-sm uppercase tracking-wide text-slate-400 border-b border-slate-700">
                    <th className="px-3 py-3 w-10"><span className="sr-only">Select</span></th>
                    <th className="px-3 py-3">Name</th>
                    <th className="px-3 py-3 text-right">Size</th>
                    <th className="px-3 py-3 hidden md:table-cell">Share</th>
                    <th className="px-3 py-3 text-right hidden sm:table-cell">Files</th>
                    <th className="px-3 py-3 text-right hidden lg:table-cell">Modified</th>
                    <th className="px-3 py-3 text-right"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/80">
                  {rows.map((n) => {
                    const rp = relPath(n);
                    const locked = protectedRow(n);
                    const deleting = deletingPaths.has(rp) || [...deletingPaths].some((p) => rp.startsWith(p + '/'));
                    return (
                      <tr key={rp} className={deleting ? 'opacity-50' : ''}>
                        <td className="px-3 py-2.5">
                          {!locked && !deleting && (
                            <input
                              type="checkbox"
                              aria-label={`Select ${n.n}`}
                              checked={selected.has(rp)}
                              onChange={() => toggle(n)}
                              className="w-5 h-5 rounded bg-slate-700 border-slate-600 text-amber-400 focus:ring-amber-400"
                            />
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          {n.t === 'd' ? (
                            <button
                              type="button"
                              onClick={() => { if (n.c) { setPath([...path, n.n]); setSelected(new Set()); } }}
                              disabled={!n.c}
                              className="inline-flex items-center gap-2 font-semibold text-amber-300 hover:underline disabled:no-underline disabled:text-slate-300 text-left"
                              title={n.c ? `Open ${n.n}` : 'Contents not listed in this scan'}
                            >
                              {locked ? <LockClosedIcon className="w-5 h-5 text-slate-400 shrink-0" /> : <FolderIcon className="w-5 h-5 shrink-0" />}
                              <span className="break-all">{n.n}</span>
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-2 text-white">
                              <DocumentIcon className="w-5 h-5 text-slate-400 shrink-0" /><span className="break-all">{n.n}</span>
                            </span>
                          )}
                          {deleting && <span className="ml-2 text-sm text-amber-300">Deleting…</span>}
                          {locked && <span className="ml-2 text-sm text-slate-400">System</span>}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap text-white">{fmtBytes(n.s)}</td>
                        <td className="px-3 py-2.5 hidden md:table-cell">
                          <div className="h-2 w-40 rounded-full bg-slate-900 overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${((100 * n.s) / rowsTotal).toFixed(1)}%` }} /></div>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums hidden sm:table-cell text-slate-300">{n.t === 'd' ? n.f ?? '—' : 1}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums hidden lg:table-cell text-slate-300 whitespace-nowrap">{fmtDate(n.m)}</td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap">
                          {!locked && !deleting && (
                            <button
                              type="button"
                              onClick={() => setConfirm([n])}
                              className="inline-flex items-center gap-1 text-sm font-medium text-red-300 hover:text-red-200 px-2 py-1.5 rounded-md hover:bg-red-900/30"
                              aria-label={`Delete ${n.n}`}
                            >
                              <TrashIcon className="w-4 h-4" /> Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* delete confirm (in-app modal — browsers suppress window.confirm) */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="sd-delete-title">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
            <h3 id="sd-delete-title" className="text-lg font-bold text-white mb-2">
              Delete {confirm.length === 1 ? `“${confirm[0].n}”` : `${confirm.length} items`} from the SD card?
            </h3>
            <p className="text-base text-slate-300 mb-2">
              This frees <span className="text-white font-semibold">{fmtBytes(confirm.reduce((a, n) => a + n.s, 0))}</span> on {deviceLabel(device as SdDeviceSummary)}. The Scout removes the files itself — this cannot be undone.
            </p>
            {confirm.length > 1 && (
              <ul className="text-sm text-slate-400 max-h-32 overflow-y-auto mb-3 list-disc pl-5">{confirm.map((n) => <li key={n.n} className="break-all">{n.n}</li>)}</ul>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={() => setConfirm(null)} className="px-4 py-2 rounded-lg bg-slate-700 text-white text-base hover:bg-slate-600">Cancel</button>
              <button
                type="button"
                onClick={() => del.mutate(confirm.map(relPath))}
                disabled={del.isPending}
                className="px-4 py-2 rounded-lg bg-red-500 text-white text-base font-bold hover:bg-red-400 disabled:opacity-60"
              >
                {del.isPending ? 'Queuing…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
